'use client';

import { useCallback, useEffect, useRef } from 'react';

import { flushSync } from 'react-dom';

import {
  DEPTH_IN_MIN_DURATION,
  DEPTH_IN_MIN_SHIFT,
  DEPTH_REVEAL_LEAD,
  DEPTH_REVEAL_MAX_WAIT,
  DEPTH_REVEAL_SAMPLE,
  SELECTOR,
} from './constants';
import { getDepthInTiming } from './dom';

/** 뎁스 행이 붙거나 빠질 때 아래로 밀려나는 요소들 */
const SHIFTED = [
  SELECTOR.quick,
  SELECTOR.pin,
  SELECTOR.sorting,
  SELECTOR.list,
].join(', ');

/** 뎁스 상태 변경을 스크롤 이동이 끝나갈 즈음 등장 모션과 함께 반영한다 */
export type RevealDepth = (
  apply: () => void,
  changesRows: boolean,
  scrollTop: number | null
) => void;

/**
 * 새 뎁스 행 등장 모션.
 *
 * 밀려난 아래 요소들(퀵필터·필터바·정렬·목록)을 원래 자리에서 시작해 제자리로
 * 미끄러뜨린다. 탭이 고정 중이면 탭 블록 높이도 같은 속도로 키우거나 줄여,
 * 새 행이 블록 경계에서 드러나게 한다.
 *
 * @param beforeHeight 변경 전 탭 블록 높이. 지금 높이와의 차가 이동 거리, 부호가 방향이다
 * @param budget 모션을 끝내야 하는 시간(ms). 없으면 CSS에 적힌 길이를 그대로 쓴다
 */
const playDepthIn = (beforeHeight: number, budget?: number): void => {
  const $wrap = document.querySelector(SELECTOR.wrap);
  const rows = $wrap?.querySelectorAll<HTMLElement>(
    `${SELECTOR.inner} ${SELECTOR.row}`
  );
  const $row = rows?.[rows.length - 1];
  const $inner = $wrap?.querySelector<HTMLElement>(SELECTOR.inner);
  if (!$wrap || !$row || !$inner) return;

  // budget이 있으면 그 안에 끝나도록 길이를 줄인다. 하한은 DEPTH_IN_MIN_DURATION
  const base = getDepthInTiming();
  const timing = {
    easing: base.easing,
    duration:
      budget === undefined
        ? base.duration
        : Math.max(Math.min(base.duration, budget), DEPTH_IN_MIN_DURATION),
  };

  // 아래가 밀려나는 거리. 늘면 양수, 줄면 음수
  const full = $inner.getBoundingClientRect().height;
  const shift = full - beforeHeight;
  if (Math.abs(shift) < DEPTH_IN_MIN_SHIFT) return;

  $wrap.querySelectorAll<HTMLElement>(SHIFTED).forEach($el => {
    $el.animate(
      [{ transform: `translateY(${-shift}px)` }, { transform: 'none' }],
      timing
    );
  });

  // 흐름에 있을 땐 새로 생긴 행만 페이드로 띄운다
  if (getComputedStyle($inner).position !== 'fixed') {
    if (shift > 0) $row.animate([{ opacity: 0 }, { opacity: 1 }], timing);
    return;
  }

  // 고정 중이면 블록이 자라거나 줄면서, 새 행은 그 경계에서 드러난다
  $inner.style.overflow = 'hidden';

  const grow = $inner.animate(
    [{ height: `${beforeHeight}px` }, { height: `${full}px` }],
    timing
  );
  const clear = () => {
    $inner.style.overflow = '';
  };
  grow.finished.then(clear).catch(clear);
};

/**
 * 남은 스크롤 이동 시간(ms)을 추정한다.
 * 이동 곡선을 ease-out 3차(p = 1 - (1-t)³)로 보고 시간 진행률 t를 구해 전체 길이를 잡는다.
 *
 * @param elapsed 실제로 움직이기 시작한 뒤 흐른 시간(ms)
 * @param gone 거리 진행률(0~1)
 * @returns 표본이 모자라면 Infinity
 */
const estimateRemaining = (elapsed: number, gone: number): number => {
  if (elapsed < DEPTH_REVEAL_SAMPLE || gone <= 0) return Infinity;

  const timeGone = 1 - Math.cbrt(1 - gone);
  if (timeGone <= 0) return Infinity;

  return elapsed / timeGone - elapsed;
};

/**
 * 하위 뎁스 노출
 *
 * 행 수가 바뀌고(changesRows) 스크롤 이동 목표(scrollTop)가 있으면, 등장 모션이
 * 도착과 함께 끝나도록 남은 이동 시간이 모션 길이만큼 남았을 때 반영한다.
 * 그 외에는 바로 반영한다.
 */
export function useRevealDepth(): RevealDepth {
  // 대기 중인 노출의 순번. 새 노출이 시작되면 이전 것은 무효가 된다
  const seqRef = useRef(0);
  // 직전 노출에서 리렌더에 쓴 시간(ms). 다음 노출을 그만큼 더 앞당긴다
  const costRef = useRef(DEPTH_REVEAL_LEAD);

  // 언마운트되면 대기 중인 노출을 무효화한다
  useEffect(
    () => () => {
      seqRef.current += 1;
    },
    []
  );

  return useCallback<RevealDepth>((apply, changesRows, scrollTop) => {
    seqRef.current += 1;
    const seq = seqRef.current;

    /** 등장 모션은 행 수가 달라질 때만 걸고, 항목만 바뀌면 그대로 반영한다 */
    const reveal = (left = Infinity) => {
      if (!changesRows) {
        apply();
        return;
      }

      const $inner = document.querySelector(SELECTOR.inner);
      // 진행 중인 앞선 모션을 끊은 뒤 변경 전 높이를 잰다
      $inner?.getAnimations().forEach(anim => anim.cancel());
      const before = $inner?.getBoundingClientRect().height ?? 0;

      // 페인트 전에 반영되도록 DOM을 동기로 갱신하고, 리렌더에 걸린 시간을 기록한다
      const renderedAt = performance.now();
      flushSync(apply);
      const cost = performance.now() - renderedAt;
      costRef.current = cost;

      // 남은 시간에서 리렌더 비용을 뺀 만큼을 모션 길이로 쓴다
      playDepthIn(
        before,
        left === Infinity ? undefined : Math.max(left - cost, 0)
      );
    };

    // 이동이 없거나 행 수가 그대로면 바로 반영한다
    if (!changesRows || scrollTop === null) {
      reveal();
      return;
    }

    const startedAt = performance.now();
    const startedFrom = window.pageYOffset;
    const total = Math.abs(startedFrom - scrollTop);
    // 남은 이동 시간이 이 값 이하로 내려오면 노출한다(모션 길이 + 리렌더 예상 시간)
    const lead =
      getDepthInTiming().duration +
      Math.max(costRef.current, DEPTH_REVEAL_LEAD);

    // 실제로 움직이기 시작한 시점. 경과 시간은 여기서부터 잰다
    let movedAt = 0;

    const tick = () => {
      if (seq !== seqRef.current) return;

      const now = performance.now();
      const rest = Math.abs(window.pageYOffset - scrollTop);
      if (!movedAt && window.pageYOffset !== startedFrom) movedAt = now;

      const elapsed = movedAt > 0 ? now - movedAt : 0;
      const gone = total > 0 ? Math.min(1 - rest / total, 0.999) : 0;
      const left = estimateRemaining(elapsed, gone);

      // 남은 시간이 lead 이하이거나, 이미 도착했거나, 너무 오래 기다렸으면 노출한다
      if (
        left <= lead ||
        rest <= 1 ||
        now - startedAt > DEPTH_REVEAL_MAX_WAIT
      ) {
        reveal(left);
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }, []);
}
